//  _        _      _         _
// | |  _  _| |_  _| |__ _  _| |__ _  _
// | |_| || | | || | '_ \ || | '_ \ || |
// |____\_,_|_|\_,_|_.__/\_,_|_.__/\_,_|
//
// Copyright © Lulububu Software GmbH - All Rights Reserved
// https://lulububu.de
//
// Unauthorized copying of this file, via any medium is strictly prohibited!
// Proprietary and confidential.

import { ColorButton }              from '@/components/stateless/atomic/ColorButton';
import ColorButtonTheme             from '@/components/stateless/atomic/ColorButton/ColorButtonTheme';
import { CountryFlag }              from '@/components/stateless/atomic/CountryFlag';
import { DropDownInput }            from '@/components/stateless/atomic/DropDownInput';
import { HeadlineMedium }           from '@/components/stateless/atomic/HeadlineMedium';
import { TextInput }                from '@/components/stateless/atomic/TextInput';
import { CheckBox }                 from '@/components/stateless/atomic/CheckBox';
import { FormRow }                  from '@/components/stateless/composed/FormRow';
import PasswordStrengthBar          from 'react-password-strength-bar';
import PasswordStrength             from '@/constants/PasswordStrength';
import CountryListHelper            from '@/helper/CountryListHelper';
import PropTypes                    from '@/components/PropTypes';
import MembershipListHelper         from '@/helper/MembershipListHelper';
import { AlertBoxActions }          from '@/store/actions/alertBox';
import { CompanyActions }           from '@/store/actions/company';
import { MembershipRequestActions } from '@/store/actions/membershipRequest';
import { SignupActions }            from '@/store/actions/signup';
import className                    from 'classnames';
import I18n                         from 'i18next';
import _                            from 'lodash';
import React                        from 'react';
import { bindActionCreators }       from 'redux';
import { compose }                  from 'redux';
import { connect }                  from 'react-redux';

import ComponentHelper from '@/helper/ComponentHelper';
import Pages           from '@/constants/Pages';
import styles          from './styles.module.scss';

class Screen extends React.Component {
    constructor(props) {
        super(props);

        this.state = this.getInitialState(props);
    }

    componentDidMount() {
        const { isClaimingCompany, claimingCompanyId } = this.props;

        if (isClaimingCompany && claimingCompanyId) {
            this.props.fetchCompany({
                id: claimingCompanyId,
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            !prevProps.signupHasSucceeded
            && this.props.signupHasSucceeded
            && !this.props.isLoggedIn
        ) {
            this.setState(this.getInitialState(this.props));
            this.props.resetSignup();
        }

        if (
            !prevProps.companyName
            && this.props.companyName
            && this.props.isClaimingCompany
        ) {
            this.setState(this.getInitialState(this.props));
        }
    }

    getInitialState = (props) => {
        const membershipOption = MembershipListHelper.getCurrentMembershipOption(
            props.membershipOptions,
            props.option,
        );
        let countryOption      = null;

        if (props.companyAddress?.countryCode) {
            const countryOptions = CountryListHelper.getOptions();

            countryOption = countryOptions.find((option) => option.value === props.companyAddress.countryCode) || null;
        }

        return (
            {
                // User signup fields
                name:             props.isLoggedIn ? props.userName : '',
                email:            props.isLoggedIn ? props.userEmail : '',
                password:         '',
                repeatedPassword: '',
                passwordScore:    0,
                // Company fields
                companyName:                     props.companyName || '',
                companyIri:                      props.companyIri,
                address:                         props.companyAddressString || '',
                country:                         countryOption,
                city:                            props.companyAddress?.city || '',
                state:                           props.companyAddress?.state || '',
                postalCode:                      props.companyAddress?.postalCode || '',
                taxId:                           null,
                membership:                      membershipOption,
                preferredOnsiteVerificationDate: null,
                promotionCode:                   null,
                termsAccepted:                   false,
                validationErrors:                {},
                showValidation:                  false,
            }
        );
    };

    membershipCanCreateCompany = (membershipOption) => {
        return membershipOption?.possibleToCreateCompany ?? false;
    };

    getRequiredFields = () => {
        const { isLoggedIn }   = this.props;
        const { membership }   = this.state;
        const canCreateCompany = this.membershipCanCreateCompany(membership);
        let requiredFields     = !isLoggedIn ?
            [
                'name',
                'email',
                'password',
                'repeatedPassword',
            ] :
            [];

        if (canCreateCompany) {
            requiredFields = [
                ...requiredFields,
                'companyName',
                'address',
                'country',
                'city',
                'state',
                'postalCode',
                'taxId',
                'membership',
            ];
        }

        return _.filter(requiredFields);
    };

    isFormValid = () => {
        const { isLoggedIn } = this.props;
        const requiredFields = this.getRequiredFields();

        if (!this.state.termsAccepted) {
            return false;
        }

        if (!isLoggedIn && this.state.password !== this.state.repeatedPassword) {
            return false;
        }

        const hasAllRequiredFields = requiredFields.every((field) => {
            const value = this.state[field];

            return value !== null && value !== undefined && value !== '';
        });

        return hasAllRequiredFields;
    };

    getValidationErrors = () => {
        const { isLoggedIn } = this.props;
        const requiredFields = this.getRequiredFields();
        const errors         = {};

        requiredFields.forEach((field) => {
            const value = this.state[field];

            if (!value || value === '' || value === null || value === undefined) {
                errors[field] = true;
            }
        });

        if (!this.state.termsAccepted) {
            errors.termsAccepted = true;
        }

        if (!isLoggedIn && this.state.password !== this.state.repeatedPassword) {
            errors.repeatedPassword = true;
        }

        return errors;
    };

    onMembershipSendClicked = () => {
        const { isLoggedIn }   = this.props;
        const validationErrors = this.getValidationErrors();
        const hasErrors        = Object.keys(validationErrors).length > 0;

        this.setState({
            validationErrors,
            showValidation: true,
        });

        if (!this.state.termsAccepted) {
            this.props.showErrorAlert({
                text: I18n.t('agbRequired'),
            });
            return;
        }

        // Check password match for new users
        if (!isLoggedIn && this.state.password !== this.state.repeatedPassword) {
            this.props.showErrorAlert({
                text: I18n.t('passwordNotMatch'),
            });
            return;
        }

        if (!hasErrors) {
            if (isLoggedIn) {
                this.props.requestMembership(this.state);
            } else {
                const signupData = {
                    name:             this.state.name,
                    identifier:       this.state.email,
                    password:         this.state.password,
                    repeatedPassword: this.state.repeatedPassword,
                    passwordScore:    this.state.passwordScore,
                    membershipData:   this.state,
                };
                this.props.signup(signupData);
            }
        } else {
            this.props.showErrorAlert({
                text: I18n.t('missingMembershipData'),
            });
        }
    };

    clearAlerts = () => {
        this.props.clearAlerts();
    };

    onFieldChange = (field) => (event) => {
        const newValidationErrors = {
            ...this.state.validationErrors,
        };
        delete newValidationErrors[field];

        this.setState({
            [field]:          event.target.value,
            validationErrors: newValidationErrors,
        });
        this.clearAlerts();
    };

    onDateChange = (date) => {
        this.setState({
            preferredOnsiteVerificationDate: new Date(date),
        });
        this.clearAlerts();
    };

    onCountryChange = (country) => {
        const newValidationErrors = {
            ...this.state.validationErrors,
        };
        delete newValidationErrors.country;

        this.setState({
            country,
            validationErrors: newValidationErrors,
        });
        this.clearAlerts();
    };

    onMembershipChange = (membership) => {
        this.setState({
            membership,
        });
        this.clearAlerts();
    };

    onTermsAcceptedChange = () => {
        const newValidationErrors = {
            ...this.state.validationErrors,
        };
        delete newValidationErrors.termsAccepted;

        this.setState({
            termsAccepted:    !this.state.termsAccepted,
            validationErrors: newValidationErrors,
        });
        this.clearAlerts();
    };

    renderCompanyCountryOption = (name, option) => {
        return (
            <div className={styles.companyLanguage}>
                <CountryFlag
                    width={16}
                    iso31661Alpha2CountryCode={option.value}
                />
                {name}
            </div>
        );
    };

    renderCompanyNameField = (isLoggedIn) => {
        const style    = this.state.showValidation && this.state.validationErrors.companyName ? styles.errorInput : '';
        const disabled = (
            isLoggedIn &&
                this.state.companyName
        )
            || this.props.isClaimingCompany;

        return (
            <FormRow
                label={I18n.t('companyName')}
                required
            >
                <TextInput
                    value={this.state.companyName}
                    onChange={this.onFieldChange('companyName')}
                    disabled={disabled}
                    className={style}
                />
            </FormRow>
        );
    };

    renderAddressField = () => {
        const style = this.state.showValidation && this.state.validationErrors.address ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('address')}
                required
            >
                <TextInput
                    value={this.state.address}
                    onChange={this.onFieldChange('address')}
                    disabled={this.props.isClaimingCompany}
                    className={style}
                />
            </FormRow>
        );
    };

    renderCountryField = () => {
        const style = this.state.showValidation && this.state.validationErrors.country ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('country')}
                required
            >
                <div className={style}>
                    <DropDownInput
                        options={CountryListHelper.getOptions()}
                        defaultValue={this.state.country}
                        renderValue={this.renderCompanyCountryOption}
                        onChange={this.onCountryChange}
                        disabled={this.props.isClaimingCompany}
                    />
                </div>
            </FormRow>
        );
    };

    renderPostalField = () => {
        const style = this.state.showValidation && this.state.validationErrors.postalCode ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('postalCode')}
                required
            >
                <TextInput
                    value={this.state.postalCode}
                    onChange={this.onFieldChange('postalCode')}
                    disabled={this.props.isClaimingCompany}
                    className={style}
                />
            </FormRow>
        );
    };

    renderCityField = () => {
        const style = this.state.showValidation && this.state.validationErrors.city ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('city')}
                required
            >
                <TextInput
                    value={this.state.city}
                    onChange={this.onFieldChange('city')}
                    disabled={this.props.isClaimingCompany}
                    className={style}
                />
            </FormRow>
        );
    };

    renderStateField = () => {
        const style = this.state.showValidation && this.state.validationErrors.state ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('state')}
                required
            >
                <TextInput
                    value={this.state.state}
                    onChange={this.onFieldChange('state')}
                    disabled={this.props.isClaimingCompany}
                    className={style}
                />
            </FormRow>
        );
    };

    renderTaxField = () => {
        const style = this.state.showValidation && this.state.validationErrors.taxId ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('taxId')}
                required
            >
                <TextInput
                    value={this.state.taxId}
                    onChange={this.onFieldChange('taxId')}
                    className={style}
                />
            </FormRow>
        );
    };

    renderNameField = () => {
        const style = this.state.showValidation && this.state.validationErrors.name ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('name')}
                required
            >
                <TextInput
                    value={this.state.name}
                    onChange={this.onFieldChange('name')}
                    className={style}
                />
            </FormRow>
        );
    };

    renderEmailField = () => {
        const style = this.state.showValidation && this.state.validationErrors.email ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('emailAddress')}
                required
            >
                <TextInput
                    type="email"
                    value={this.state.email}
                    onChange={this.onFieldChange('email')}
                    className={style}
                />
            </FormRow>
        );
    };

    renderPasswordField = () => {
        const { password } = this.state;
        const style        = this.state.showValidation && this.state.validationErrors.password ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('password')}
                required
            >
                <div>
                    <TextInput
                        type="password"
                        value={password}
                        onChange={this.onFieldChange('password')}
                        className={style}
                    />
                    {this.renderPasswordStrengthBar(password)}
                </div>
            </FormRow>
        );
    };

    renderPasswordStrengthBar = (password) => {
        if (!password) {
            return null;
        }

        return (
            <PasswordStrengthBar
                password={password}
                minLength={PasswordStrength.minimalPasswordLength}
                onChangeScore={(score) => this.setState({
                    passwordScore: score,
                })}
            />
        );
    };

    renderRepeatPasswordField = () => {
        const { repeatedPassword, password } = this.state;
        const style                          = this.state.showValidation && this.state.validationErrors.repeatedPassword ? styles.errorInput : '';

        return (
            <FormRow
                label={I18n.t('repeatedPassword')}
                required
            >
                <div>
                    <TextInput
                        type="password"
                        value={repeatedPassword}
                        onChange={this.onFieldChange('repeatedPassword')}
                        className={style}
                    />
                    {this.renderRepeatPasswordStrengthBar(repeatedPassword, password)}
                </div>
            </FormRow>
        );
    };

    renderRepeatPasswordStrengthBar = (repeatedPassword, password) => {
        if (!repeatedPassword || repeatedPassword === password) {
            return null;
        }

        return (
            <PasswordStrengthBar
                password={''}
                barColors={['#ef4836']}
                minLength={0}
                scoreWords={[
                    I18n.t('passwordDoesNotMatch'),
                ]}
            />
        );
    };

    renderCompanyFields = () => {
        const { isLoggedIn }   = this.props;
        const { membership }   = this.state;
        const canCreateCompany = this.membershipCanCreateCompany(membership);

        if (!canCreateCompany) {
            return null;
        }

        const headlineText = isLoggedIn
            ? I18n.t('membershipOptions')
            : I18n.t('companyInformation');

        return (
            <>
                <HeadlineMedium text={headlineText} />
                <div className={styles.row}>
                    {this.renderCompanyNameField(isLoggedIn)}
                    {this.renderAddressField(isLoggedIn)}
                </div>
                <div className={styles.row}>
                    {this.renderPostalField()}
                    {this.renderCityField()}
                </div>
                <div className={styles.row}>
                    {this.renderStateField()}
                    {this.renderCountryField()}
                </div>
                <div className={className(styles.row, styles.rowHalf)}>
                    {this.renderTaxField()}
                </div>
            </>
        );
    };

    renderSignupFields = () => {
        if (this.props.isLoggedIn) {
            return null;
        }

        return (
            <>
                <HeadlineMedium text={I18n.t('accountInformation')} />
                <div className={styles.row}>
                    {this.renderNameField()}
                    {this.renderEmailField()}
                </div>
                <div className={styles.row}>
                    {this.renderPasswordField()}
                    {this.renderRepeatPasswordField()}
                </div>
                <div className={styles.sectionSeparator}></div>
            </>
        );
    };

    render() {
        const { isLoggedIn } = this.props;
        const { membership } = this.state;
        let buttonText       = 'signUpPaidMembership';

        if (isLoggedIn) {
            buttonText = 'upgradeToPaidMembership';
        } else if (!membership.priceInEuro) {
            buttonText = 'signUpFreeMembership';
        }

        return (
            <div className={styles.membershipContainer}>
                <div className={styles.form}>
                    <div className={styles.inputs}>
                        {this.renderSignupFields()}
                        {this.renderCompanyFields()}
                        <div className={className(styles.row, styles.rowHalf)}>
                            <FormRow label={I18n.t('promotionCode')}>
                                <TextInput
                                    value={this.state.promotionCode}
                                    onChange={this.onFieldChange('promotionCode')}
                                />
                            </FormRow>
                        </div>
                    </div>
                    <div className={styles.agbSection}>
                        <CheckBox
                            checked={this.state.termsAccepted}
                            text={
                                <span>
                                    {I18n.t('agbAcceptance')}{' '}
                                    <a
                                        href={Pages.termsAndConditions}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {I18n.t('agbTermsAndConditions')}
                                    </a>
                                </span>
                            }
                            valueChanged={this.onTermsAcceptedChange}
                        />
                    </div>
                    <div className={styles.requiredFieldsHint}>
                        {I18n.t('fieldsWithAsteriskRequired', 'Fields with * are required')}
                    </div>
                    <hr />
                    <ColorButton
                        theme={ColorButtonTheme.orange}
                        onClick={this.onMembershipSendClicked}
                        text={I18n.t(buttonText)}
                        disabled={!this.isFormValid()}
                    />
                </div>
            </div>
        );
    }

    shouldComponentUpdate(nextProps, nextState) {
        return ComponentHelper.shouldComponentUpdate(
            this,
            nextProps,
            nextState,
        );
    }
}

Screen.propTypes = {
    claimingCompanyId:    PropTypes.string,
    clearAlerts:          PropTypes.func,
    companyAddress:       PropTypes.object,
    companyAddressString: PropTypes.object,
    companyIri:           PropTypes.string,
    companyName:          PropTypes.string,
    fetchCompany:         PropTypes.func,
    isClaimingCompany:    PropTypes.bool,
    isLoggedIn:           PropTypes.bool,
    membershipOptions:    PropTypes.array,
    option:               PropTypes.string,
    requestMembership:    PropTypes.func,
    resetSignup:          PropTypes.func,
    showErrorAlert:       PropTypes.func,
    signup:               PropTypes.func,
    signupHasSucceeded:   PropTypes.bool,
    userEmail:            PropTypes.string,
    userName:             PropTypes.string,
};

Screen.defaultProps = {
    claimingCompanyId:    null,
    clearAlerts:          _.noop,
    companyAddress:       null,
    companyAddressString: null,
    companyIri:           null,
    companyName:          null,
    fetchCompany:         _.noop,
    isClaimingCompany:    false,
    isLoggedIn:           false,
    membershipOptions:    [],
    option:               null,
    requestMembership:    _.noop,
    resetSignup:          _.noop,
    showErrorAlert:       _.noop,
    signup:               _.noop,
    signupHasSucceeded:   false,
    userEmail:            null,
    userName:             null,
};

Screen.renderAffectingProps = Object.keys(Screen.defaultProps);

Screen.renderAffectingStates = [
    'name',
    'email',
    'password',
    'repeatedPassword',
    'companyName',
    'address',
    'country',
    'city',
    'state',
    'region',
    'taxId',
    'membership',
    'preferredOnsiteVerificationDate',
    'promotionCode',
    'termsAccepted',
    'validationErrors',
    'showValidation',
];

const mapDispatchToProps = (dispatch) => bindActionCreators(
    {
        ...MembershipRequestActions,
        ...SignupActions,
        ...AlertBoxActions,
        fetchCompany: CompanyActions.fetchCompany,
    },
    dispatch,
);

const mapStateToProps = (state) => {
    const isLoggedIn           = !_.isEmpty(_.get(state, 'user.token', null));
    const userCompanyId        = _.get(state, 'user.company');
    const claimingCompanyId    = _.get(state, 'signup.company');
    const companyId            = claimingCompanyId || userCompanyId;
    const company              = _.get(state, ['company', 'companies', companyId]);
    const companyIri           = _.get(company, 'iri');
    const companyName          = _.get(company, 'name');
    const companyAddress       = _.get(company, 'address');
    const companyAddressString = `${companyAddress?.street ?? ''} ${companyAddress?.houseNumber ?? ''}`;
    const membershipOptions    = _.get(state, 'membership.options', []);
    const option               = state.router?.location?.query?.option;
    const userName             = _.get(state, 'user.name');
    const userEmail            = _.get(state, 'user.identifier');
    const signupHasSucceeded   = _.get(state, 'signup.hasSucceeded', false);
    const isClaimingCompany    = !!claimingCompanyId;

    return {
        claimingCompanyId,
        companyIri,
        companyName,
        companyAddress,
        companyAddressString,
        isLoggedIn,
        isClaimingCompany,
        membershipOptions,
        option,
        signupHasSucceeded,
        userEmail,
        userName,
    };
};

export default compose(connect(
    mapStateToProps,
    mapDispatchToProps,
))(Screen);
